import { Clear, Refresh, Search } from '@mui/icons-material';
import { Autocomplete, Box, CircularProgress, IconButton, InputAdornment, TextField } from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { PendingRespondentStatus, PendingRespondentStatusMap, SearchParams } from 'src/@types/pendingRespondentStatus';
import { ShowDeletableSwitchBase } from './ShowDeletableSwitch';
import { useIdIssuanceManagementSearchForm } from './hooks/useIdIssuanceManagementSearchForm';
import { Action } from './utils/searchState';

type IdIssuanceManagementSearchFormProps = {
  statusMap: PendingRespondentStatusMap;
  fetchPendingRespondents: (searchParams: SearchParams) => Promise<void>;
  searchParams: SearchParams;
  dispatch: Dispatch<Action>;
  loading: boolean;
};

export type StatusOption = {
  id: 'ALL' | PendingRespondentStatus;
  label: string;
};

export const IdIssuanceManagementSearchForm = ({
  statusMap,
  fetchPendingRespondents,
  searchParams,
  dispatch,
  loading,
}: IdIssuanceManagementSearchFormProps) => {
  const { t } = useTranslation();
  const { onSearchTextChange, onStatusChange, onDeletableChange, refresh, clearSearchText } = useIdIssuanceManagementSearchForm({
    fetchPendingRespondents,
    searchParams,
    dispatch,
  });

  const options: StatusOption[] = Object.entries(statusMap).map(([id, label]) => ({
    id: id as 'ALL' | PendingRespondentStatus,
    label,
  }));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2, pb: 2 }}>
      <TextField
        value={searchParams.searchText}
        placeholder={t('idIssuanceManagement.searchForm.placeHolder')}
        variant="outlined"
        fullWidth
        autoComplete="off"
        size="small"
        onChange={onSearchTextChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {searchParams.searchText && (
                <IconButton size="small" onClick={clearSearchText}>
                  <Clear fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      <Autocomplete
        options={options}
        value={options.find((option) => option.id === searchParams.status)}
        disableClearable={true}
        getOptionLabel={(option) => {
          return option.label;
        }}
        isOptionEqualToValue={(option, value) => {
          return option.label === value.label;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              ...params.InputProps,
              readOnly: true,
            }}
          />
        )}
        onChange={onStatusChange}
        sx={{
          minWidth: 200,
        }}
      />
      <ShowDeletableSwitchBase checked={searchParams.deletable} onChange={onDeletableChange} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
        <IconButton onClick={refresh} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : <Refresh sx={{ fontSize: 24 }} />}
        </IconButton>
      </Box>
    </Box>
  );
};
